import {utils} from '@wallaby-ui/core';
import {compact, get, find, forEach, isArray, pickBy} from 'lodash';
import * as React from 'react';

import {AUTO_LAYOUT_COMPONENTS_MAP} from './auto_layout_components_map';
import InputComponentWrapperContainer from 'src/js/containers/auto_layout/components/dynamic/InputComponentWrapperContainer';
import {
  KEYWORD_PROP_CONSTRAINTS_RULE_TYPE,
  KEYWORD_PROP_CONSTRAINTS_RULE_TYPE_ENUM,
  KEYWORD_PROP_REF,
  KEYWORD_PROP_CONSTRAINTS_LIST_REF,
  KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE,
  KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE_XHR,
} from 'src/js/model/auto_layout_content_schema';
import {ScreenDataHelpers} from 'src/js/model/auto_layout_screen_data_helpers';

/**
 * @typedef {Object} RendererParameters
 * @property {ContentSchema} rootContent
 * @property {LayoutSchema} rootLayout
 * @property {Object} rootData
 * @property {ContentComponent} content
 * @property {LayoutComponent} layout
 * @property {LayoutComponentLayoutItems} layoutItems
 * @property {string} pathToData
 * @property {boolean} readOnly
 * @property {boolean} textOnly
 * @property {boolean} expanded
 */

class LayoutRenderer {
  static joinDataPath(args) {
    return compact(args).join('.');
  }

  static isInputComponent(layoutComponent) {
    return layoutComponent.inputComponent;
  }

  static filterDataChanges(pathToData, dataChanges) {
    // just simple prefix, it can return more than is necessary (possible to optimize)
    return dataChanges && pathToData
      ? pickBy(dataChanges, (value, key) => key.indexOf(pathToData) === 0)
      : undefined;
  }

  static getLayoutComponentFromMap(layoutItem) {
    const layoutComponentName = layoutItem.getDefaultComponent().getId();
    return AUTO_LAYOUT_COMPONENTS_MAP()[layoutComponentName];
  }

  static getElement(layoutItem, inputComponent = false) {
    const layoutComponent =
      LayoutRenderer.getLayoutComponentFromMap(layoutItem);
    return inputComponent && LayoutRenderer.isInputComponent(layoutComponent)
      ? InputComponentWrapperContainer
      : layoutComponent.component;
  }

  static getProps(layoutItem, parentProps) {
    const contentProperty = layoutItem.getContentProperty();
    const pathToData = LayoutRenderer.joinDataPath([
      parentProps.pathToData,
      contentProperty,
    ]);
    // TODO: BY-ACTION create data property
    const data = pathToData
      ? get(parentProps.rootData, pathToData)
      : parentProps.rootData;
    const content = parentProps.metadata
      .getContent()
      .getComponent(parentProps.content.getId())
      .getProperty(contentProperty);
    const layout = parentProps.metadata
      .getLayout()
      .getComponent(parentProps.layout.getId())
      .getProperty(layoutItem.getName());
    const dataChanges = LayoutRenderer.filterDataChanges(
      pathToData,
      parentProps.dataChanges
    );
    return {...parentProps, data, pathToData, content, layout, dataChanges};
  }

  static getChildren(layoutItem, childProps) {
    const layoutComponent =
      LayoutRenderer.getLayoutComponentFromMap(layoutItem);
    return layoutComponent.inputComponent
      ? React.createElement(layoutComponent.component, childProps)
      : [];
  }

  static getArrayItemProps(layoutItemKeys, parentProps, arrayItemIdx) {
    const arrayData = get(parentProps.rootData, parentProps.pathToData, []);

    // TODO: BY-ACTION if data property does not exist, it should be generated
    const pathToData = `${parentProps.pathToData}[${arrayItemIdx}]`;
    if (!get(parentProps.rootData, pathToData)) {
      // add empty data object in parent data object
      arrayData[arrayItemIdx] = {};
      const newId = utils.generateGUID();
      forEach(layoutItemKeys, (key) => {
        const itemLayoutItem = parentProps.layout
          .getLayoutItems()
          .getLayoutItem(key);
        const itemContentProperty = itemLayoutItem.getContentProperty();
        arrayData[arrayItemIdx][itemContentProperty] =
          ScreenDataHelpers.createEmptyDataProperty(
            parentProps.content.getProperty(itemContentProperty),
            parentProps.metadata.getContent()
          );
        arrayData[arrayItemIdx][itemContentProperty].$id = newId;
      });
    }

    const data = get(parentProps.rootData, pathToData);
    const dataChanges = LayoutRenderer.filterDataChanges(
      pathToData,
      parentProps.dataChanges
    );
    return {...parentProps, pathToData, data, arrayItemIdx, dataChanges};
  }

  static getConstraintEnum(content, inputName) {
    // TODO: refactor to use content (constraint) abstraction (see content_schema.js)
    const constraints = find(content.getConstraints(), {
      [KEYWORD_PROP_CONSTRAINTS_RULE_TYPE]:
        KEYWORD_PROP_CONSTRAINTS_RULE_TYPE_ENUM,
    });

    // type checking
    if (typeof constraints === 'undefined') {
      throw new Error(
        `Bad content definition. Missing required Enum ruleType for input "${inputName}".`
      );
    }
    return constraints;
  }

  static isSelectAsync(content, inputName) {
    const constraints = LayoutRenderer.getConstraintEnum(content, inputName);
    return (
      constraints[KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE] ===
      KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE_XHR
    );
  }

  static getSelectOptions(rootContent, content, inputName) {
    let options = [];
    const constraints = LayoutRenderer.getConstraintEnum(content, inputName);
    const fetchedItems = rootContent.getComponent(
      constraints[KEYWORD_PROP_REF]
    );

    if (fetchedItems && isArray(fetchedItems.items)) {
      options = fetchedItems.items;
    } else {
      throw new Error(
        `Method RootContent.getComponent doesn't return array in input "${inputName}".`
      );
    }
    return options;
  }

  static getSelectListId(content, inputName) {
    const constraints = LayoutRenderer.getConstraintEnum(content, inputName);
    return constraints[KEYWORD_PROP_CONSTRAINTS_LIST_REF];
  }
}

export default LayoutRenderer;
